import React, { useMemo } from 'react';
import { max as d3Max } from 'd3-array';
import { scaleBand, scaleLinear } from 'd3-scale';
import { stack as d3Stack } from 'd3-shape';
import {
  AxisConfig,
  ChartMargin,
} from '../charts/chart-types';
import { chartTokens } from '../charts/chart-tokens';
import { formatCompact, sumValues, truncateLabel } from '../charts/chart-utils';
import './bar-chart.scss';

export type BarVariant = 'grouped' | 'stacked' | 'horizontal-stacked';

export interface BarSeries {
  /** Stable key matching a property on each `BarDatum.values` record. */
  key: string;
  /** Display label used in ARIA descriptions. */
  label: string;
  /** Optional fill override. Falls back to the chart palette in token order. */
  color?: string;
}

export interface BarDatum {
  /** Category label (string for ordinal axes). */
  category: string;
  /** Numeric value per series key. Missing keys render as 0. */
  values: Record<string, number>;
}

export interface BarChartProps {
  data: BarDatum[];
  series: BarSeries[];
  variant: BarVariant;
  width: number;
  height: number;
  margin?: ChartMargin;
  xAxis?: AxisConfig<string | number>;
  yAxis?: AxisConfig<number>;
  /** Bar corner radius in px. Default `2` matches the cmx-argus dashboard. */
  cornerRadius?: number;
  /**
   * Horizontal-stacked only: clip the rendered rows to this many entries.
   * Data is sorted descending by total before clipping.
   */
  maxRows?: number;
  /**
   * Horizontal-stacked only: render a compact total label at the right end of
   * each row (e.g. `'5.7K'`). Default `false`.
   */
  valueLabels?: boolean;
  /** Required ARIA label for the SVG. */
  ariaLabel: string;
  /** Optional longer description, rendered into a `<desc>` element. */
  ariaDescription?: string;
  /** Fired when the user hovers a single bar segment. */
  onBarHover?: (datum: BarDatum, seriesKey: string) => void;
}

const DEFAULT_VERTICAL_MARGIN: ChartMargin = { top: 16, right: 24, bottom: 32, left: 48 };
const DEFAULT_HORIZONTAL_MARGIN: ChartMargin = { top: 16, right: 24, bottom: 32, left: 120 };

const defaultMarginFor = (variant: BarVariant): ChartMargin =>
  variant === 'horizontal-stacked'
    ? DEFAULT_HORIZONTAL_MARGIN
    : DEFAULT_VERTICAL_MARGIN;

const defaultPalette = [
  chartTokens.series.automated,
  chartTokens.series.manual,
  chartTokens.series.accentBlue,
  chartTokens.series.accentCream,
];

const seriesColor = (s: BarSeries, idx: number): string =>
  s.color ?? defaultPalette[idx % defaultPalette.length];

/**
 * Presentational bar chart with three configurable variants:
 * - `grouped`: side-by-side bars per category (Automation Trend panel)
 * - `stacked`: vertical stacks per category (cmx-argus parity)
 * - `horizontal-stacked`: rows of stacked bars (Volume by Case Type panel)
 *
 * Pure rendering only — no data fetching, filtering, or tooltip rendering.
 */
export const BarChart: React.FC<BarChartProps> = ({
  data,
  series,
  variant,
  width,
  height,
  margin,
  xAxis,
  yAxis,
  cornerRadius = 2,
  maxRows,
  valueLabels = false,
  ariaLabel,
  ariaDescription,
  onBarHover,
}) => {
  const resolvedMargin = margin ?? defaultMarginFor(variant);
  const innerWidth = Math.max(0, width - resolvedMargin.left - resolvedMargin.right);
  const innerHeight = Math.max(0, height - resolvedMargin.top - resolvedMargin.bottom);

  const isHorizontal = variant === 'horizontal-stacked';

  const preparedData = useMemo(() => {
    if (!isHorizontal) return data;
    const withTotals = data
      .map((d) => ({ datum: d, total: sumValues(d.values) }))
      .sort((a, b) => b.total - a.total);
    const limit = maxRows ?? withTotals.length;
    return withTotals.slice(0, limit).map((x) => x.datum);
  }, [data, isHorizontal, maxRows]);

  const seriesKeys = series.map((s) => s.key);

  const stackedRows = useMemo(() => {
    if (variant === 'grouped') return null;
    return d3Stack<BarDatum>()
      .keys(seriesKeys)
      .value((d, key) => d.values[key] ?? 0)(preparedData);
  }, [variant, seriesKeys, preparedData]);

  const maxStackedTotal = useMemo(
    () => d3Max(preparedData, (d) => sumValues(d.values)) ?? 0,
    [preparedData]
  );

  const maxGroupedValue = useMemo(
    () =>
      d3Max(
        preparedData.flatMap((d) => seriesKeys.map((k) => d.values[k] ?? 0))
      ) ?? 0,
    [preparedData, seriesKeys]
  );

  const valueDomainMax = variant === 'grouped' ? maxGroupedValue : maxStackedTotal;

  const categoryScale = scaleBand<string>()
    .domain(preparedData.map((d) => d.category))
    .range(isHorizontal ? [0, innerHeight] : [0, innerWidth])
    .padding(isHorizontal ? 0.2 : 0.15);

  const valueScale = scaleLinear()
    .domain([0, valueDomainMax || 1])
    .nice()
    .range(isHorizontal ? [0, innerWidth] : [innerHeight, 0]);

  const innerSeriesScale =
    variant === 'grouped'
      ? scaleBand<string>()
          .domain(seriesKeys)
          .range([0, categoryScale.bandwidth()])
          .padding(0.05)
      : null;

  const valueTickFormat = yAxis?.tickFormat ?? formatCompact;
  const categoryTickFormat = xAxis?.tickFormat ?? ((v: string | number) => String(v));

  const showXAxis = xAxis?.show !== false;
  const showYAxis = yAxis?.show !== false;

  const renderValueAxis = () => {
    if (isHorizontal ? !showXAxis : !showYAxis) return null;
    const ticks = valueScale.ticks(5);
    return (
      <g
        className="bar-chart__axis bar-chart__axis--value"
        aria-hidden="true"
        transform={isHorizontal ? `translate(0,${innerHeight})` : undefined}
      >
        {ticks.map((t) => {
          if (isHorizontal) {
            const x = valueScale(t);
            return (
              <g key={t} transform={`translate(${x},0)`}>
                <line
                  className="bar-chart__gridline"
                  y1={-innerHeight}
                  y2={0}
                />
                <text className="bar-chart__axis-text" y={16} textAnchor="middle">
                  {valueTickFormat(t)}
                </text>
              </g>
            );
          }
          const y = valueScale(t);
          return (
            <g key={t} transform={`translate(0,${y})`}>
              <line className="bar-chart__gridline" x1={0} x2={innerWidth} />
              <text
                className="bar-chart__axis-text"
                x={-8}
                dy="0.32em"
                textAnchor="end"
              >
                {valueTickFormat(t)}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  const renderCategoryAxis = () => {
    if (isHorizontal ? !showYAxis : !showXAxis) return null;
    return (
      <g className="bar-chart__axis bar-chart__axis--category" aria-hidden="true">
        {preparedData.map((d) => {
          const c = categoryScale(d.category);
          if (c == null) return null;
          if (isHorizontal) {
            const y = c + categoryScale.bandwidth() / 2;
            return (
              <text
                key={d.category}
                className="bar-chart__axis-text"
                x={-8}
                y={y}
                dy="0.32em"
                textAnchor="end"
              >
                {truncateLabel(categoryTickFormat(d.category))}
              </text>
            );
          }
          const x = c + categoryScale.bandwidth() / 2;
          return (
            <text
              key={d.category}
              className="bar-chart__axis-text"
              x={x}
              y={innerHeight + 18}
              textAnchor="middle"
            >
              {categoryTickFormat(d.category)}
            </text>
          );
        })}
      </g>
    );
  };

  const renderGroupedBars = () =>
    preparedData.flatMap((d) => {
      const cx = categoryScale(d.category);
      if (cx == null || !innerSeriesScale) return [];
      return series.map((s, idx) => {
        const value = d.values[s.key] ?? 0;
        const ix = innerSeriesScale(s.key) ?? 0;
        const y = valueScale(value);
        const barHeight = innerHeight - y;
        return (
          <rect
            key={`${d.category}-${s.key}`}
            className="bar-chart__bar"
            role="graphics-symbol"
            aria-label={`${d.category}, ${s.label}: ${valueTickFormat(value)}`}
            x={cx + ix}
            y={y}
            width={innerSeriesScale.bandwidth()}
            height={barHeight}
            rx={cornerRadius}
            fill={seriesColor(s, idx)}
            onMouseEnter={onBarHover ? () => onBarHover(d, s.key) : undefined}
          />
        );
      });
    });

  const renderStackedBars = () => {
    if (!stackedRows) return null;
    const bars = stackedRows.flatMap((seriesRow, sIdx) => {
      const s = series[sIdx];
      return seriesRow.map((seg) => {
        const datum = seg.data;
        const c = categoryScale(datum.category);
        if (c == null) return null;
        if (isHorizontal) {
          const x = valueScale(seg[0]);
          const w = Math.max(0, valueScale(seg[1]) - x);
          return (
            <rect
              key={`${datum.category}-${s.key}`}
              className="bar-chart__bar"
              role="graphics-symbol"
              aria-label={`${datum.category}, ${s.label}: ${valueTickFormat(
                datum.values[s.key] ?? 0
              )}`}
              x={x}
              y={c}
              width={w}
              height={categoryScale.bandwidth()}
              rx={cornerRadius}
              fill={seriesColor(s, sIdx)}
              onMouseEnter={
                onBarHover ? () => onBarHover(datum, s.key) : undefined
              }
            />
          );
        }
        const y = valueScale(seg[1]);
        const h = Math.max(0, valueScale(seg[0]) - y);
        return (
          <rect
            key={`${datum.category}-${s.key}`}
            className="bar-chart__bar"
            role="graphics-symbol"
            aria-label={`${datum.category}, ${s.label}: ${valueTickFormat(
              datum.values[s.key] ?? 0
            )}`}
            x={c}
            y={y}
            width={categoryScale.bandwidth()}
            height={h}
            rx={cornerRadius}
            fill={seriesColor(s, sIdx)}
            onMouseEnter={
              onBarHover ? () => onBarHover(datum, s.key) : undefined
            }
          />
        );
      });
    });

    const labels =
      valueLabels && isHorizontal
        ? preparedData.map((d) => {
            const total = sumValues(d.values);
            const c = categoryScale(d.category);
            if (c == null) return null;
            const labelX = valueScale(total) + 4;
            const labelY = c + categoryScale.bandwidth() / 2;
            return (
              <text
                key={`label-${d.category}`}
                className="bar-chart__axis-text"
                x={labelX}
                y={labelY}
                dy="0.32em"
                aria-hidden="true"
              >
                {formatCompact(total)}
              </text>
            );
          })
        : null;

    return (
      <>
        {bars}
        {labels}
      </>
    );
  };

  return (
    <svg
      className="bar-chart"
      role="img"
      width={width}
      height={height}
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
    >
      <title>{ariaLabel}</title>
      {ariaDescription ? <desc>{ariaDescription}</desc> : null}
      <g transform={`translate(${resolvedMargin.left},${resolvedMargin.top})`}>
        {renderValueAxis()}
        {renderCategoryAxis()}
        {variant === 'grouped' ? renderGroupedBars() : renderStackedBars()}
      </g>
    </svg>
  );
};

BarChart.displayName = 'BarChart';
